import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, ArrowDownRight, Pencil, Trash2, Wand2, X } from 'lucide-react';

const ExpensesListTab = ({
  expenses,
  expenseCategories = [],
  formatCurrency,
  onAddExpense,
  onEditExpense,
  onDeleteExpense,
  onBulkRecategorize,
}) => {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  // Clear selection whenever the underlying expense list changes (e.g., after sync/refresh)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [expenses]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = expenses.length > 0 && selectedIds.size === expenses.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(expenses.map((e) => e.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedVendorCount = useMemo(() => {
    const vendors = new Set();
    expenses.forEach((e) => {
      if (selectedIds.has(e.id) && (e.vendor || '').trim()) {
        vendors.add(e.vendor.trim().toLowerCase());
      }
    });
    return vendors.size;
  }, [expenses, selectedIds]);

  const handleApply = async () => {
    if (!bulkCategoryId || selectedIds.size === 0 || !onBulkRecategorize) return;
    setIsApplying(true);
    try {
      await onBulkRecategorize(Array.from(selectedIds), bulkCategoryId);
      setBulkCategoryId('');
      setSelectedIds(new Set());
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <CardTitle>Expenses</CardTitle>
          <CardDescription>Track business expenses by category</CardDescription>
        </div>
        <Button
          onClick={onAddExpense}
          className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
          data-testid="add-expense-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Expense
        </Button>
      </CardHeader>

      {/* Bulk action toolbar — appears only when rows are selected */}
      {selectedIds.size > 0 && (
        <div
          className="mx-6 mb-2 flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-md bg-[#F4F1EA] border border-[#D5CFC1]"
          data-testid="bulk-toolbar"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-[#0A2B1D]">
            <Wand2 className="w-4 h-4" />
            <span data-testid="bulk-selected-count">
              {selectedIds.size} selected
              {selectedVendorCount > 0 && ` • ${selectedVendorCount} unique vendor${selectedVendorCount === 1 ? '' : 's'}`}
            </span>
          </div>
          <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:items-center">
            <Select value={bulkCategoryId} onValueChange={setBulkCategoryId}>
              <SelectTrigger
                className="sm:w-[260px] bg-white"
                data-testid="bulk-category-select"
              >
                <SelectValue placeholder="Choose category..." />
              </SelectTrigger>
              <SelectContent>
                {expenseCategories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleApply}
              disabled={!bulkCategoryId || isApplying}
              className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
              data-testid="bulk-apply-btn"
            >
              {isApplying ? 'Applying...' : `Re-categorize ${selectedIds.size}`}
            </Button>
            <Button
              variant="ghost"
              onClick={clearSelection}
              className="text-[#5C6661]"
              data-testid="bulk-clear-btn"
            >
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </div>
      )}

      <CardContent>
        {expenses.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <ArrowDownRight className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No expenses recorded for this period</p>
            <p className="text-sm mt-1">Add expenses manually or connect your bank</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-3 w-8">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Select all expenses"
                      data-testid="bulk-select-all"
                      className={someSelected ? 'data-[state=unchecked]:bg-[#0A2B1D]/20' : ''}
                    />
                  </th>
                  <th className="pb-3 font-medium text-gray-600">Date</th>
                  <th className="pb-3 font-medium text-gray-600">Category</th>
                  <th className="pb-3 font-medium text-gray-600">Description</th>
                  <th className="pb-3 font-medium text-gray-600">Vendor</th>
                  <th className="pb-3 font-medium text-gray-600 text-right">Amount</th>
                  <th className="pb-3 font-medium text-gray-600 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((exp) => {
                  const checked = selectedIds.has(exp.id);
                  return (
                    <tr
                      key={exp.id}
                      className={`border-b hover:bg-gray-50 ${checked ? 'bg-[#F4F1EA]' : ''}`}
                    >
                      <td className="py-3">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleOne(exp.id)}
                          aria-label={`Select expense ${exp.description}`}
                          data-testid={`bulk-select-${exp.id}`}
                        />
                      </td>
                      <td className="py-3 text-sm">{exp.date}</td>
                      <td className="py-3">
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                          {exp.category_name}
                        </span>
                      </td>
                      <td className="py-3 text-sm">{exp.description}</td>
                      <td className="py-3 text-sm text-gray-500">{exp.vendor || '-'}</td>
                      <td className="py-3 text-right font-medium text-red-600">
                        {formatCurrency(exp.amount)}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#5C6661] hover:text-[#0A2B1D] h-8 w-8 p-0"
                            onClick={() => onEditExpense(exp, 'expense')}
                            data-testid={`edit-expense-${exp.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                            onClick={() => onDeleteExpense(exp.id)}
                            data-testid={`delete-expense-${exp.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50">
                  <td />
                  <td colSpan={4} className="py-3 font-semibold">Total</td>
                  <td className="py-3 text-right font-bold text-red-600">
                    {formatCurrency(expenses.reduce((sum, e) => sum + e.amount, 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExpensesListTab;
